import { Flex, Tabs, type TabsProps } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnalyticsDialogsRequest } from '@shared/api';
import { Text } from '@shared/ui/antd';

import { useQuestionsStats } from '../../hooks/use-questions-stats';

import {
  buildTopicOptions,
  EMPTY_TOPICS,
  type TopicOption,
} from './lib/dialog-summary-utils';
import { DialogSummaryQuestionsTab } from './ui/dialog-summary-questions-tab';
import { DialogSummaryTopicsTab } from './ui/dialog-summary-topics-tab';
import styles from './dialog-summary.module.scss';

type DialogSummaryTab = 'topics' | 'questions';

type Props = {
  request: AnalyticsDialogsRequest | null;
  enabled: boolean;
};

export const DialogSummary = ({ request, enabled }: Props) => {
  const { t } = useTranslation('translation');
  const [activeTab, setActiveTab] = useState<DialogSummaryTab>('topics');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [openTopics, setOpenTopics] = useState<string[]>([]);

  const isQuestionsTab = activeTab === 'questions' || activeTab === 'topics';
  const questionsStats = useQuestionsStats(request, enabled && isQuestionsTab);

  const topics = questionsStats.data ?? EMPTY_TOPICS;
  const topicOptions = useMemo<TopicOption[]>(() => buildTopicOptions(topics), [topics]);

  const filteredTopics = useMemo(() => {
    if (!selectedTopics.length) return topics;
    const selected = new Set(selectedTopics);
    return topics.filter((topic) => selected.has(topic.topic));
  }, [selectedTopics, topics]);

  const visibleOpenTopics = useMemo(() => {
    if (!openTopics.length || !filteredTopics.length) return new Set<string>();
    const available = new Set(filteredTopics.map((item) => item.topic));
    return new Set(openTopics.filter((topic) => available.has(topic)));
  }, [filteredTopics, openTopics]);

  const handleTabChange = useCallback(
    (key: string) => {
      const nextTab = key as DialogSummaryTab;
      setActiveTab(nextTab);
      if (!enabled || !request) return;
      if (nextTab === 'questions' || nextTab === 'topics') {
        questionsStats.refetch();
      }
    },
    [enabled, questionsStats, request],
  );

  const handleTopicChange = useCallback((value: unknown) => {
    const next = Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
    setSelectedTopics(next);
  }, []);

  const toggleTopic = useCallback((topic: string) => {
    setOpenTopics((prev) =>
      prev.includes(topic) ? prev.filter((item) => item !== topic) : [...prev, topic],
    );
  }, []);

  const topicsPlaceholder = t('analytics.summary.topicsPlaceholder');
  const noDataLabel = t('analytics.summary.noData');

  const tabs: TabsProps['items'] = [
    {
      key: 'topics',
      label: t('analytics.summary.tabs.topics'),
      children: (
        <DialogSummaryTopicsTab
          isLoading={questionsStats.isLoading}
          isError={questionsStats.isError}
          refetch={questionsStats.refetch}
          topicsPlaceholder={topicsPlaceholder}
          noDataLabel={noDataLabel}
          selectedTopics={selectedTopics}
          topicOptions={topicOptions}
          filteredTopics={filteredTopics}
          visibleOpenTopics={visibleOpenTopics}
          onTopicChange={handleTopicChange}
          onToggleTopic={toggleTopic}
        />
      ),
    },
    {
      key: 'questions',
      label: t('analytics.summary.tabs.questions'),
      children: (
        <DialogSummaryQuestionsTab
          isLoading={questionsStats.isLoading}
          isError={questionsStats.isError}
          refetch={questionsStats.refetch}
          topicsPlaceholder={topicsPlaceholder}
          noDataLabel={noDataLabel}
          selectedTopics={selectedTopics}
          topicOptions={topicOptions}
          filteredTopics={filteredTopics}
          onTopicChange={handleTopicChange}
        />
      ),
    },
  ];

  if (!enabled || !request) return null;

  return (
    <Flex vertical className={styles.root} gap={16}>
      <Flex align="center" justify="space-between">
        <Text variant="xl" fontWeight={500} style={{ color: 'var(--color-text-primary)' }}>
          {t('analytics.summary.title')}
        </Text>
      </Flex>

      <Tabs className={styles.tabs} activeKey={activeTab} onChange={handleTabChange} items={tabs} />
    </Flex>
  );
};

